import { Injectable } from "@angular/core";
import {
  addDoc,
  collection,
  collectionData,
  deleteDoc,
  doc,
  DocumentData,
  Firestore,
  getDoc,
  getDocs,
  limit,
  onSnapshot,
  orderBy,
  query,
  QueryDocumentSnapshot,
  startAfter,
  startAt,
  updateDoc,
} from "@angular/fire/firestore";
import { from, map, Observable, of, Subject } from "rxjs";
import { ICategory } from "../interfaces/category.interface";

@Injectable({
  providedIn: "root",
})
export class CategoryService {
  private itemsPerPage = 5;
  private lastDoc: QueryDocumentSnapshot<DocumentData> | null = null;
  private firstDoc: QueryDocumentSnapshot<DocumentData> | null = null;
  private lastVisibleDocs: QueryDocumentSnapshot<DocumentData>[] = [];
  hasNextPage = false;
  hasPreviousPage = false;

  private collectionRef;
  private collectionName = "categories";
  private fieldSort = "description";

  //Para efetuar uma ação em outro componente
  private actionSource = new Subject<string>();
  action$ = this.actionSource.asObservable();

  constructor(private firestore: Firestore) {
    this.collectionRef = collection(this.firestore, this.collectionName);
  }

  //Enviar ação para o componente alvo
  sendAction(action: string) {
    this.actionSource.next(action);
  }

  async create(category: ICategory) {
    const docRef = await addDoc(this.collectionRef, category);
    return await updateDoc(docRef, { id: docRef.id });
  }

  async update(id: string, data: any) {
    const docRef = doc(this.firestore, this.collectionName, id);
    return await updateDoc(docRef, data);
  }

  async delete(id: string) {
    const docRef = doc(this.firestore, this.collectionName, id);
    return deleteDoc(docRef);
  }

  async getById(id: string) {
    const docRef = doc(this.firestore, this.collectionName, id);
    const snapshot = await getDoc(docRef);
    if (snapshot.exists()) {
      return snapshot.data();
    } else {
      return null;
    }
  }

  getAll(): Observable<any[]> {
    return collectionData(this.collectionRef, { idField: "id" });
  }

  getFirstPage(): Observable<DocumentData[]> {
    const colRef = collection(this.firestore, this.collectionName);
    const q = query(
      colRef,
      orderBy(this.fieldSort, "asc"),
      limit(this.itemsPerPage)
    );

    return from(getDocs(q)).pipe(
      map((snapshot) => {
        if (!snapshot.empty) {
          this.firstDoc = snapshot.docs[0];
          this.lastDoc = snapshot.docs[snapshot.docs.length - 1];
          this.lastVisibleDocs = [this.firstDoc];
          this.hasPreviousPage = false;
          this.hasNextPage = snapshot.docs.length === this.itemsPerPage; // Se pegamos o máximo, pode haver mais
        } else {
          this.hasNextPage = false;
          this.hasPreviousPage = false;
        }
        return snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
      })
    );
  }

  getNextPage(): Observable<DocumentData[]> {
    if (!this.lastDoc) return new Observable();

    const colRef = collection(this.firestore, this.collectionName);
    const q = query(
      colRef,
      orderBy(this.fieldSort, "asc"),
      startAfter(this.lastDoc),
      limit(this.itemsPerPage)
    );

    return from(getDocs(q)).pipe(
      map((snapshot) => {
        if (!snapshot.empty) {
          this.firstDoc = snapshot.docs[0];
          this.lastDoc = snapshot.docs[snapshot.docs.length - 1];
          this.lastVisibleDocs.push(this.firstDoc);
          this.hasPreviousPage = true;
          this.hasNextPage = snapshot.docs.length === this.itemsPerPage;
        } else {
          this.hasNextPage = false;
        }
        return snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
      })
    );
  }

  getPreviousPage(): Observable<DocumentData[]> {
    if (this.lastVisibleDocs.length < 2) return new Observable();

    const previousDoc = this.lastVisibleDocs[this.lastVisibleDocs.length - 2];
    this.lastVisibleDocs.pop();

    const colRef = collection(this.firestore, this.collectionName);
    const q = query(
      colRef,
      orderBy(this.fieldSort, "asc"),
      startAt(previousDoc),
      limit(this.itemsPerPage)
    );

    return from(getDocs(q)).pipe(
      map((snapshot) => {
        if (!snapshot.empty) {
          this.firstDoc = snapshot.docs[0];
          this.lastDoc = snapshot.docs[snapshot.docs.length - 1];
          this.hasPreviousPage = this.lastVisibleDocs.length > 1;
          this.hasNextPage = true;
        }
        return snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
      })
    );
  }
}
